import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Snackbar, Alert, Paper, Divider, MenuItem,
  Switch, FormControlLabel,
} from "@mui/material";
import { Switch as MuiSwitch } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AdminPageHeader   from "../../components/admin/AdminPageHeader";
import AdminGridToolbar  from "../../components/admin/AdminGridToolbar";
import AdminFormDialog   from "../../components/admin/AdminFormDialog";
import { useAuth }       from "../../context/AuthContext";
import { API_BASE_URL }  from "../../config/api";

const ALL_ROLES = ["SUPER", "ADMIN", "ANALISTA_I", "ANALISTA_II"];

const ALLOWED_CREATE = {
  SUPER:      ALL_ROLES,
  ADMIN:      ["ANALISTA_I", "ANALISTA_II"],
  ANALISTA_I: [],
  ANALISTA_II:[],
};

const EMPTY = { email: "", name: "", role: "ANALISTA_I", password: "", companies: [], is_active: true };

const GRID_SX = {
  border: 0,
  "& .MuiDataGrid-columnHeaders": { backgroundColor: "rgba(0,0,0,0.03)", borderBottom: "1px solid rgba(0,0,0,0.12)" },
  "& .MuiDataGrid-row":           { borderBottom: "1px solid rgba(0,0,0,0.06)" },
  "& .MuiDataGrid-cell":          { outline: "none" },
};

export default function UsersPage() {
  const { user: me, authFetch }         = useAuth();
  const [rows,      setRows]            = useState([]);
  const [companies, setCompanies]       = useState([]);
  const [loading,   setLoading]         = useState(false);
  const [rowSel,    setRowSel]          = useState({ type: "include", ids: new Set() });
  const [dialogOpen,setDialogOpen]      = useState(false);
  const [deleteOpen,setDeleteOpen]      = useState(false);
  const [editing,   setEditing]         = useState(null);
  const [form,      setForm]            = useState(EMPTY);
  const [saving,    setSaving]          = useState(false);
  const [snack,     setSnack]           = useState({ open: false, msg: "", severity: "success" });

  const selectedIds = useMemo(() => Array.from(rowSel.ids), [rowSel]);
  const selectedRow = useMemo(() => rows.find(r => r.id === selectedIds[0]), [rows, selectedIds]);

  const showSnack = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API_BASE_URL}/admin/users`);
      if (!r.ok) throw new Error("Erro ao buscar usuários");
      setRows(await r.json());
    } catch { showSnack("Erro ao buscar usuários", "error"); }
    finally  { setLoading(false); }
  }, [authFetch]);

  const loadCompanies = useCallback(async () => {
    try {
      const r = await authFetch(`${API_BASE_URL}/admin/company/assignable`);
      if (!r.ok) throw new Error("Erro ao buscar empresas");
      setCompanies(await r.json());
    } catch {}
  }, [authFetch]);

  useEffect(() => { load(); loadCompanies(); }, [load, loadCompanies]);

  const openNew  = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = () => {
    if (!selectedRow) return;
    setEditing(selectedRow);
    setForm({
      email:     selectedRow.email,
      name:      selectedRow.name,
      role:      selectedRow.role,
      password:  "",
      is_active: selectedRow.is_active,
      companies: selectedRow.companies.map(c => c.cnpj),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.email || !form.name || !form.role) return showSnack("Preencha todos os campos obrigatórios", "error");
    if (!editing && !form.password) return showSnack("Senha obrigatória para novo usuário", "error");
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.password) delete body.password;
      const url    = editing ? `${API_BASE_URL}/admin/users/${editing.id}` : `${API_BASE_URL}/admin/users`;
      const method = editing ? "PUT" : "POST";
      const r      = await authFetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const data   = await r.json();
      if (!r.ok) throw new Error(data.error);
      showSnack(editing ? "Usuário atualizado" : "Usuário criado");
      setDialogOpen(false);
      load();
    } catch (err) { showSnack(err.message, "error"); }
    finally       { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedIds.length) return;
    setSaving(true);
    try {
      await Promise.all(selectedIds.map(id => authFetch(`${API_BASE_URL}/admin/users/${id}`, { method: "DELETE" })));
      showSnack(selectedIds.length > 1 ? `${selectedIds.length} usuários desativados` : "Usuário desativado");
      setDeleteOpen(false);
      setRowSel({ type: "include", ids: new Set() });
      load();
    } catch { showSnack("Erro ao desativar usuário(s)", "error"); }
    finally  { setSaving(false); }
  };

  const creatableRoles = ALLOWED_CREATE[me?.role] ?? [];
  const availableCompanies = me?.role === "SUPER" ? companies : companies.filter(c => me?.companies?.includes(c.cnpj));

  const columns = [
    { field: "name",      headerName: "Nome",    flex: 1 },
    { field: "email",     headerName: "E-mail",  flex: 1 },
    { field: "role", headerName: "Perfil", width: 130 },
    {
      field: "companies", headerName: "Empresas", flex: 1,
      valueGetter: (_, row) => row.companies?.map(c => c.company?.company_name || c.cnpj).join(", ") || "—",
    },
    {
      field: "is_active", headerName: "Ativo", width: 80,
      renderCell: ({ value }) => <MuiSwitch checked={!!value} size="small" disableRipple sx={{ pointerEvents: "none" }} />,
    },
  ];

  const f  = field => ({ value: form[field] ?? "", onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });

  return (
    <Box sx={{ p: 2 }}>
      <AdminPageHeader title="Usuários e Acessos" />

      <Paper variant="outlined">
        <Divider />
        <DataGrid
          rows={rows} columns={columns} getRowId={r => r.id}
          loading={loading} autoHeight checkboxSelection disableRowSelectionOnClick
          density="compact" showToolbar
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowSelectionModel={rowSel}
          onRowSelectionModelChange={setRowSel}
          slots={{ toolbar: AdminGridToolbar }}
          slotProps={{ toolbar: {
            title: "Usuários Cadastrados",
            onNew: openNew, onEdit: openEdit, onDelete: () => setDeleteOpen(true),
            canEdit: selectedIds.length === 1, canDelete: selectedIds.length > 0,
          }}}
          sx={GRID_SX}
        />
      </Paper>

      <AdminFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleSave} saving={saving}
        title={editing ? "Editar Usuário" : "Novo Usuário"}>
        <TextField label="Nome *" {...f("name")} size="small" InputLabelProps={{ shrink: true }} />
        <TextField label="E-mail *" type="email" {...f("email")} disabled={!!editing} size="small" InputLabelProps={{ shrink: true }} />
        <TextField label="Senha" type="password" {...f("password")} size="small" InputLabelProps={{ shrink: true }}
          helperText={editing ? "Deixe em branco para manter a senha atual" : "Obrigatório"} />
        <TextField select label="Perfil *" {...f("role")} size="small" InputLabelProps={{ shrink: true }}>
          {creatableRoles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </TextField>
        <TextField select label="Empresas" size="small" InputLabelProps={{ shrink: true }}
          SelectProps={{ multiple: true, renderValue: sel => sel.join(", ") }}
          value={form.companies} onChange={e => setForm(p => ({ ...p, companies: e.target.value }))}>
          {availableCompanies.map(c => (
            <MenuItem key={c.cnpj} value={c.cnpj}>{c.cnpj} — {c.company_name}</MenuItem>
          ))}
        </TextField>
        {editing && (
          <FormControlLabel
            control={<Switch checked={!!form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />}
            label="Usuário ativo" />
        )}
      </AdminFormDialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 14, pb: 1 }}>Confirmar desativação</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {selectedIds.length > 1
              ? `Desativar ${selectedIds.length} usuários selecionados? Eles perderão o acesso ao sistema.`
              : <>Desativar o usuário <strong>{selectedRow?.name}</strong>? O usuário perderá o acesso ao sistema.</>}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving} sx={{ textTransform: "none", fontSize: 13 }}>Cancelar</Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ textTransform: "none", fontSize: 13, color: "#d32f2f" }}>
            {saving ? "Desativando..." : "Desativar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
